// HRMS — Self-service: Announcements, Helpdesk + Companies setup
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import hr from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Textarea, Table, Pill, fmtDate, fmtDateTime, statusTone, SOFT } from '../ui';

export function Announcements({ companyId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'general' });
  const reload = async () => { if (companyId) setRows(await hr.get('/announcements', { company_id: companyId })); };
  useEffect(() => { reload(); }, [companyId]);
  const save = async () => {
    try {
      if (!form.title || !form.body) return toast.error('Title and body required');
      await hr.post('/announcements', { ...form, company_id: companyId });
      toast.success('Published'); setOpen(false); setForm({ category: 'general' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div data-testid="hr-announce">
      <PageHeader title="Announcements" subtitle="Company-wide updates"
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="hr-new-ann">+ Announce</Btn>} />
      <div className="space-y-3">
        {!rows.length ? <p className="text-white/40 italic text-sm">No announcements yet.</p> :
          rows.map((a) => (
            <div key={a.id} className={`${SOFT} rounded-xl p-5`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium">{a.title}</h3>
                    {a.pinned && <Pill tone="orange">Pinned</Pill>}
                    <Pill tone="blue">{a.category}</Pill>
                  </div>
                  <p className="text-white/60 text-sm mt-2 whitespace-pre-wrap">{a.body}</p>
                  <div className="text-xs text-white/35 mt-2">{fmtDateTime(a.created_at)} · {a.created_by}</div>
                </div>
                <button className="text-xs text-rose-400/70 hover:text-rose-400" onClick={async () => { await hr.del(`/announcements/${a.id}`); reload(); }}>Delete</button>
              </div>
            </div>
          ))
        }
      </div>
      <Drawer open={open} onClose={() => setOpen(false)} title="New Announcement" width="max-w-2xl">
        <Input label="Title *" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Textarea label="Body *" value={form.body || ''} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="general">General</option><option value="policy">Policy</option>
            <option value="holiday">Holiday</option><option value="celebration">Celebration</option>
          </Select>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={!!form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} />
            <span className="text-sm text-white/80">Pin to top</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save}>Publish</Btn></div>
      </Drawer>
    </div>
  );
}

export function Helpdesk({ companyId }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ priority: 'medium', category: 'general' });
  const reload = async () => {
    if (companyId) {
      setRows(await hr.get('/tickets', { company_id: companyId }));
      setEmployees(await hr.get('/employees', { company_id: companyId }));
    }
  };
  useEffect(() => { reload(); }, [companyId]);
  const save = async () => {
    try {
      if (!form.employee_id || !form.subject) return toast.error('Employee and subject required');
      await hr.post('/tickets', form);
      toast.success('Submitted'); setOpen(false); setForm({ priority: 'medium', category: 'general' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const resolve = async (id) => { await hr.post(`/tickets/${id}/resolve`, { resolution: 'Resolved' }); reload(); };
  const empMap = Object.fromEntries(employees.map((e) => [e.id, e.full_name]));

  return (
    <div data-testid="hr-helpdesk">
      <PageHeader title="HR Helpdesk" subtitle="Employee support tickets"
        right={<Btn variant="accent" onClick={() => setOpen(true)}>+ Raise Ticket</Btn>} />
      <Table
        columns={[
          { key: 'ticket_no', label: '#', render: (r) => <span className="font-mono text-xs text-white/70">{r.ticket_no}</span> },
          { key: 'employee_id', label: 'Employee', render: (r) => empMap[r.employee_id] || '—' },
          { key: 'subject', label: 'Subject' },
          { key: 'category', label: 'Category', render: (r) => <Pill tone="blue">{r.category}</Pill> },
          { key: 'priority', label: 'Priority', render: (r) => <Pill tone={r.priority === 'high' ? 'red' : r.priority === 'medium' ? 'orange' : 'neutral'}>{r.priority}</Pill> },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
          { key: 'a', label: '', render: (r) => r.status === 'open' ? <button className="text-xs text-emerald-300" onClick={() => resolve(r.id)}>Resolve</button> : null },
        ]}
        rows={rows} empty="No tickets."
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="Raise Ticket" width="max-w-xl">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Employee *" value={form.employee_id || ''} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
            <option value="">—</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </Select>
          <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="general">General</option><option value="leave">Leave</option>
            <option value="payroll">Payroll</option><option value="it">IT</option>
            <option value="hr">HR</option><option value="attendance">Attendance</option>
          </Select>
          <Select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </Select>
        </div>
        <Input label="Subject *" value={form.subject || ''} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        <Textarea label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save}>Submit</Btn></div>
      </Drawer>
    </div>
  );
}

// ─── Companies & Branches ──────────────────────────────────────────────────
export function CompaniesSetup({ companies, onChange, companyId }) {
  const [rows, setRows] = useState(companies);
  const [branches, setBranches] = useState([]);
  const [openC, setOpenC] = useState(false);
  const [openB, setOpenB] = useState(false);
  const [cForm, setCForm] = useState({ country: 'India', currency: 'INR', fy_start_month: 4 });
  const [bForm, setBForm] = useState({});
  useEffect(() => { setRows(companies); }, [companies]);
  useEffect(() => { if (companyId) hr.get('/branches', { company_id: companyId }).then(setBranches); }, [companyId]);

  const reload = async () => { setRows(await hr.get('/companies')); onChange?.(); };
  const saveC = async () => {
    try {
      if (!cForm.name) return toast.error('Name required');
      await hr.post('/companies', cForm);
      toast.success('Saved'); setOpenC(false); setCForm({ country: 'India', currency: 'INR', fy_start_month: 4 }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const saveB = async () => {
    try {
      if (!bForm.name) return toast.error('Name required');
      await hr.post('/branches', { ...bForm, company_id: companyId });
      toast.success('Saved'); setOpenB(false); setBForm({});
      if (companyId) setBranches(await hr.get('/branches', { company_id: companyId }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="hr-companies">
      <PageHeader title="Companies & Branches" subtitle="Multi-company setup · branches/locations"
        right={<div className="flex gap-2">
          <Btn variant="ghost" onClick={() => setOpenB(true)} data-testid="hr-new-branch">+ Branch</Btn>
          <Btn variant="accent" onClick={() => setOpenC(true)} data-testid="hr-new-company">+ Company</Btn>
        </div>} />

      <h3 className="text-sm text-white/55 mb-2 tracking-[0.15em] uppercase">Companies</h3>
      <Table
        columns={[
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'code', label: 'Code' },
          { key: 'gstin', label: 'GSTIN' },
          { key: 'country', label: 'Country' },
          { key: 'currency', label: 'Currency' },
          { key: 'employees', label: 'Employees', align: 'right', render: (r) => <span className="text-emerald-300">{r.employees}</span> },
        ]}
        rows={rows}
        testidPrefix="company"
      />

      <h3 className="text-sm text-white/55 mb-2 mt-6 tracking-[0.15em] uppercase">Branches (current company)</h3>
      <Table
        columns={[
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'code', label: 'Code' },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
          { key: 'gstin', label: 'GSTIN' },
        ]}
        rows={branches} empty="No branches yet."
      />

      <Drawer open={openC} onClose={() => setOpenC(false)} title="New Company" width="max-w-2xl">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={cForm.name || ''} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} />
          <Input label="Legal Name" value={cForm.legal_name || ''} onChange={(e) => setCForm({ ...cForm, legal_name: e.target.value })} />
          <Input label="Code" value={cForm.code || ''} onChange={(e) => setCForm({ ...cForm, code: e.target.value })} />
          <Input label="GSTIN" value={cForm.gstin || ''} onChange={(e) => setCForm({ ...cForm, gstin: e.target.value })} />
          <Input label="PAN" value={cForm.pan || ''} onChange={(e) => setCForm({ ...cForm, pan: e.target.value })} />
          <Input label="Country" value={cForm.country} onChange={(e) => setCForm({ ...cForm, country: e.target.value })} />
          <Input label="Currency" value={cForm.currency} onChange={(e) => setCForm({ ...cForm, currency: e.target.value })} />
          <Input label="Currency Symbol" value={cForm.currency_symbol || '₹'} onChange={(e) => setCForm({ ...cForm, currency_symbol: e.target.value })} />
        </div>
        <Textarea label="Address" value={cForm.address || ''} onChange={(e) => setCForm({ ...cForm, address: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenC(false)}>Cancel</Btn><Btn variant="accent" onClick={saveC}>Save</Btn></div>
      </Drawer>

      <Drawer open={openB} onClose={() => setOpenB(false)} title="New Branch" width="max-w-2xl">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={bForm.name || ''} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} />
          <Input label="Code" value={bForm.code || ''} onChange={(e) => setBForm({ ...bForm, code: e.target.value })} />
          <Input label="City" value={bForm.city || ''} onChange={(e) => setBForm({ ...bForm, city: e.target.value })} />
          <Input label="State" value={bForm.state || ''} onChange={(e) => setBForm({ ...bForm, state: e.target.value })} />
          <Input label="GSTIN" value={bForm.gstin || ''} onChange={(e) => setBForm({ ...bForm, gstin: e.target.value })} />
          <Input label="Timezone" value={bForm.timezone || 'Asia/Kolkata'} onChange={(e) => setBForm({ ...bForm, timezone: e.target.value })} />
        </div>
        <Textarea label="Address" value={bForm.address || ''} onChange={(e) => setBForm({ ...bForm, address: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpenB(false)}>Cancel</Btn><Btn variant="accent" onClick={saveB}>Save</Btn></div>
      </Drawer>
    </div>
  );
}
